/**
 * Audit Log Page - view all user actions
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, Loader2, Search, MessageSquare, FileText, LogIn, Settings, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import apiClient from '@/services/api/client';

const ACTION_ICONS: Record<string, any> = {
  client_chat: MessageSquare,
  multi_client_query: MessageSquare,
  document_upload: FileText,
  document_view: Eye,
  export: FileText,
  login: LogIn,
  setting_change: Settings,
};

const ACTION_LABELS: Record<string, string> = {
  client_chat: 'Client Chat',
  multi_client_query: 'Multi-Client Query',
  document_upload: 'Document Upload',
  document_view: 'Document View',
  export: 'Export',
  login: 'Login',
  setting_change: 'Setting Change',
};

export default function AuditLogPage() {
  const [actionFilter, setActionFilter] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', actionFilter, page],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50', offset: String(page * 50) });
      if (actionFilter) params.set('action', actionFilter);
      const r = await apiClient.get(`/audit-log?${params}`);
      return r.data.data;
    },
  });

  const entries = data?.entries || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Shield}
        title="Audit Trail"
        description="Complete log of all user actions and data access"
      />


      {/* Filters */}
      <div className="flex gap-3">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All actions</SelectItem>
            <SelectItem value="client_chat">Client Chat</SelectItem>
            <SelectItem value="multi_client_query">Multi-Client Query</SelectItem>
            <SelectItem value="document_upload">Document Upload</SelectItem>
            <SelectItem value="export">Export</SelectItem>
            <SelectItem value="login">Login</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground flex items-center">{total} entries</div>
      </div>

      {/* Log Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Shield className="h-8 w-8 mx-auto mb-3 opacity-30" />
              <p>No audit entries yet. Actions will be logged as users interact with the system.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Time</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">User</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Action</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">Details</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">IP</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry: any) => {
                  const Icon = ACTION_ICONS[entry.action] || Shield;
                  return (
                    <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(entry.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 text-xs">{entry.user_email || entry.user_id?.substring(0, 8)}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-[10px] gap-1">
                          <Icon className="h-3 w-3" />
                          {ACTION_LABELS[entry.action] || entry.action}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">
                        {entry.details?.query || entry.details?.clientName || entry.resource_id || '-'}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{entry.ip_address || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 50 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>Previous</Button>
          <span className="text-sm text-muted-foreground flex items-center">Page {page + 1} of {Math.ceil(total / 50)}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 50 >= total}>Next</Button>
        </div>
      )}
    </div>
  );
}
